/**
 * Sorts an array so that items matching `isSelected` appear first,
 * preserving the relative order within each group (stable sort).
 *
 * Sorting is based on the already-applied filter state (not draft state),
 * so the order is frozen at the moment the panel is opened and does not
 * re-sort as the user toggles items within the session.
 *
 * @param items      - The full item list (e.g. options from the API)
 * @param isSelected - Predicate returning true for items that should bubble up
 * @returns          A new array with selected items first
 */
export function sortSelectedFirst<T>(items: T[], isSelected: (item: T) => boolean): T[] {
  if (items.length === 0) return items;
  const selected: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (isSelected(item)) {
      selected.push(item);
    } else {
      rest.push(item);
    }
  }
  return selected.length > 0 ? [...selected, ...rest] : items;
}

/**
 * Returns a new array sorted by a single field.
 *
 * Extracted verbatim from `TransactionStore.flatPayments` (behaviour preserved
 * exactly). Comparison uses the natural `<`/`>` operators on the mapped field
 * values (already normalised to primitives by the row mapper).
 *
 * Null/undefined handling matches the original comparator: `null`/`undefined`
 * values sort to the **end** in ascending order and to the **front** in
 * descending order.
 *
 * @param items     - Rows to sort (not mutated)
 * @param field     - Property name to sort on
 * @param direction - `'asc'` or `'desc'`
 * @returns A new, sorted array
 */
export function sortByField<T extends Record<string, unknown>>(
  items: T[],
  field: string,
  direction: 'asc' | 'desc',
): T[] {
  const factor = direction === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (av == null) return factor;
    if (bv == null) return -factor;
    if (av < bv) return -factor;
    if (av > bv) return factor;
    return 0;
  });
}
